<script setup lang="ts">
import type { LocationsSize } from '../locations.vue'
import { CustomControl, GoogleMap } from 'vue3-google-map'
import SlideoverButton from '../list/slideover-button.vue'

const props = defineProps<{
  size: LocationsSize
}>()

const { locations, activeLocation } = useMapState()!

const color = useColorMode()

const mapId = computed(() => {
  if (color.value === 'dark')
    return '815ed64194c1f938'

  return '459ac89d964cd5c8'
})

const center: google.maps.LatLngLiteral = {
  lat: 51.738554,
  lng: 5.251218,
}

const tokenGoogleMaps = useRuntimeConfig().public.tokenGoogleMaps

const componentKey = ref(0)

watch(color, () => {
  componentKey.value += 1
})

const mapRef = ref()

function panToWithOffset(position: google.maps.LatLngLiteral) {
  const approximateInfoWindowHeight = 350
  const offsetY = approximateInfoWindowHeight / 2
  const map = mapRef.value.map
  const scale = 2 ** map.getZoom()
  const worldCoordinateCenter = map.getProjection().fromLatLngToPoint(position)
  const pixelOffset = new google.maps.Point(0 / scale, offsetY / scale)
  const newCenter = map.getProjection().fromPointToLatLng(
    new google.maps.Point(
      worldCoordinateCenter.x,
      worldCoordinateCenter.y - pixelOffset.y,
    ),
  )

  mapRef.value.map.panTo(newCenter)
}

const _maxZoom = 8

function fitMapToMarkers() {
  if (!mapRef.value || locations.value.length === 0)
    return

  const bounds = new google.maps.LatLngBounds()
  locations.value.forEach(location => bounds.extend(location.position))

  mapRef.value.map.fitBounds(
    bounds,
    {
      top: 25,
      right: 25,
      bottom: 25,
      left: 25,
    },
  )

  google.maps.event.addListenerOnce(mapRef.value.map, 'bounds_changed', () => {
    if (mapRef.value.map.getZoom() > _maxZoom)
      mapRef.value.map.setZoom(_maxZoom)
  })
}

const ready = computed<boolean>(() => Boolean(mapRef.value?.ready))

watch(ready, () => {
  if (!ready.value)
    return
  fitMapToMarkers()
  google.maps.event.trigger(mapRef.value.map, 'resize')
})

watch([ready, activeLocation], ([ready, location]) => {
  if (!ready || !location)
    return

  panToWithOffset(location.position)
})
</script>

<template>
  <GoogleMap
    ref="mapRef"
    :key="componentKey"
    :api-key="tokenGoogleMaps"
    :center="center"
    :map-id="mapId"
    :map-type-control="false"
    :street-view-control="false"
    :fullscreen-control="false"
    :clickable-icons="false"
    :zoom-control="true"
    :zoom="8"
  >
    <template v-if="ready">
      <CustomControl v-if="props.size === 'small' && locations.length > 1" position="TOP_RIGHT">
        <div class="p-2">
          <SlideoverButton />
        </div>
      </CustomControl>
      <LocationsMapMarker v-for="location in locations" :key="location.id" :location="location" />
    </template>
  </GoogleMap>
</template>
